/**
 * The pieces every surface service shares, moved from the shell's `services/service-kit.ts` and
 * `services/refusal.ts` (kernel apps, WS-14 section 3.6; layered shell surfaces, WS-8 section 3.1,
 * R-9, R-22): the session port each host implements, the clock, the per-mount scope, the listener
 * set, the read-only session view and the one refusal class.
 *
 * Both host documents import it: the shell on `<dp>` (WS-16) and the kernel SPA on `kernel.<dp>`
 * (WS-15). Nothing here imports `react`, `react-router` or `@zion/sdk-react`. The per-mount request
 * and the provider boundary are the v1 contract's `SurfaceMountRequest` and
 * `SurfaceServicesProvider`, imported from `@zion/sdk-surfaces/v1` and never restated.
 */
import {
  SurfaceServiceRefusedError,
  type SurfaceMountRequest,
  type SurfaceSessionService,
} from '@zion/sdk-surfaces/v1';

/**
 * The one error a surface can receive from a host service, re-exported from its one home in
 * `@zion/sdk-surfaces/v1` `contract.ts` (R-81 a). This package declares no copy of the class, the
 * reason type or the reason list.
 *
 * - `SurfaceServiceRefusedError`: `name` is always `'SurfaceServiceRefusedError'`, `reason` is one
 *   of the reasons, and `message` is the reason, a colon, a space and the detail
 *   (`'root-only: polarity.set is the routed root's'`). The detail names the member and never
 *   carries the refused value, so no caller data reaches a log through the message.
 * - `SurfaceServiceRefusalReason`: the contract's `RefusalReason`, under the name the services
 *   already read.
 * - `SURFACE_SERVICE_REFUSAL_REASONS`: every reason, frozen, in the contract's order.
 *
 * A surface bundles no copy of this class, so it tells a refusal apart by `name` and `reason`,
 * never by `instanceof`. Every service in this package throws or rejects with this class and with
 * no other error of its own, except the host defects that throw `TypeError`.
 */
export {
  SURFACE_SERVICE_REFUSAL_REASONS,
  SurfaceServiceRefusedError,
  type RefusalReason as SurfaceServiceRefusalReason,
} from '@zion/sdk-surfaces/v1';

/**
 * One frozen view of the session, as each host's session builder maps it (WS-16 over the shell's
 * auth core, WS-15 over the kernel token).
 *
 * - `hydrating`: the boot refresh has not settled; every other field is `null` or `[]`.
 * - `anonymous`: signed out; every other field is `null` or `[]`.
 * - `authenticated`: `token`, `userId` and `orgId` are strings; roles and permissions are the
 *   access token's org claims.
 */
export interface ServiceSessionSnapshot {
  readonly status: 'hydrating' | 'anonymous' | 'authenticated';
  readonly token: string | null;
  readonly userId: string | null;
  readonly orgId: string | null;
  readonly orgRoles: readonly string[];
  readonly orgPermissions: readonly string[];
}

/**
 * The session port a host hands the services. This package declares it; each host implements it
 * over its own origin's session, and no credential of one origin enters the other (decision 10).
 *
 * `current()` returns the latest snapshot; a caller must not mutate it. `subscribe` calls the
 * listener after any field changes, a token refresh included, and returns an idempotent
 * unsubscribe.
 */
export interface ServiceSession {
  current(): ServiceSessionSnapshot;
  subscribe(listener: () => void): () => void;
}

/**
 * Time, as the services read it. `now` is epoch milliseconds. `setTimeout` arms a one-shot timer
 * and returns its handle; `clearTimeout` cancels it, and an unknown or fired handle is a no-op.
 */
export interface ServiceClock {
  now(): number;
  setTimeout(callback: () => void, ms: number): number;
  clearTimeout(handle: number): void;
}

/** The production clock: `Date.now` and the window's timers. */
export const systemClock: ServiceClock = Object.freeze({
  now: () => Date.now(),
  setTimeout: (callback: () => void, ms: number) => window.setTimeout(callback, ms),
  clearTimeout: (handle: number) => window.clearTimeout(handle),
});

/**
 * One mount's lifetime, as every service view sees it.
 *
 * `own` queues a cleanup that runs once at dispose, in reverse order of registration; a cleanup
 * registered after dispose runs at once. `guard` wraps a service object so that, after dispose,
 * every function member throws {@link SurfaceServiceRefusedError} `disposed`, and every member
 * named in `asyncMembers` returns a promise rejected with it instead. Before dispose the wrapper
 * calls through with the arguments unchanged. Non-function members are copied as they are.
 */
export interface MountScope {
  readonly request: SurfaceMountRequest;
  readonly disposed: boolean;
  own(cleanup: () => void): void;
  guard<T extends object>(service: T, asyncMembers?: readonly (keyof T & string)[]): T;
}

/** The scope plus the one call that ends it, held by the host's provider alone. */
export interface MountScopeController extends MountScope {
  /**
   * End the mount: set `disposed`, then run every owned cleanup once in reverse order. A cleanup
   * that throws is reported to `console.error` and the rest still run. A second call is a no-op.
   */
  dispose(): void;
}

/** The refusal every member answers after its mount ended. */
export function disposedError(member: string): SurfaceServiceRefusedError {
  return new SurfaceServiceRefusedError('disposed', `${member} after the mount ended`);
}

/** Build the scope for one mount request. It starts live and owns nothing. */
export function createMountScope(request: SurfaceMountRequest): MountScopeController {
  const cleanups: (() => void)[] = [];
  let disposed = false;
  const runSafely = (cleanup: () => void): void => {
    try {
      cleanup();
    } catch (error) {
      console.error('[surface-services] a mount cleanup threw', error);
    }
  };
  const scope: MountScopeController = {
    request,
    get disposed() {
      return disposed;
    },
    own(cleanup) {
      if (disposed) {
        runSafely(cleanup);
        return;
      }
      cleanups.push(cleanup);
    },
    guard<T extends object>(service: T, asyncMembers: readonly (keyof T & string)[] = []): T {
      const rejecting = new Set<string>(asyncMembers);
      const copy: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(service)) {
        if (typeof value !== 'function') {
          copy[key] = value;
          continue;
        }
        const member = value as (...args: unknown[]) => unknown;
        copy[key] = (...args: unknown[]): unknown => {
          if (disposed) {
            if (rejecting.has(key)) return Promise.reject(disposedError(key));
            throw disposedError(key);
          }
          return member.apply(service, args);
        };
      }
      return Object.freeze(copy) as T;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      while (cleanups.length > 0) runSafely(cleanups.pop() as () => void);
    },
  };
  return scope;
}

/**
 * An ordered set of change listeners. `add` returns an idempotent removal. `notify` calls every
 * listener present when it starts, in insertion order; a throw is reported to `console.error`
 * and the rest still run. `clear` removes every listener.
 */
export interface ListenerSet {
  add(listener: () => void): () => void;
  notify(): void;
  clear(): void;
  /** How many listeners are registered now. */
  readonly size: number;
}

/** Build an empty {@link ListenerSet}. */
export function createListenerSet(): ListenerSet {
  const listeners = new Set<() => void>();
  return {
    add(listener) {
      // Wrap so the same function added twice is two registrations with two removals.
      const entry = (): void => listener();
      listeners.add(entry);
      return () => {
        listeners.delete(entry);
      };
    },
    notify() {
      for (const listener of Array.from(listeners)) {
        try {
          listener();
        } catch (error) {
          console.error('[surface-services] a change listener threw', error);
        }
      }
    },
    clear() {
      listeners.clear();
    },
    get size() {
      return listeners.size;
    },
  };
}

/**
 * The mount's read-only session view (R-22's `session` member): `{ orgId }` from the current
 * snapshot, `null` unless the session is authenticated. Each call returns a fresh frozen object.
 * After dispose `current` throws `disposed`.
 */
export function createSessionView(scope: MountScope, session: ServiceSession): SurfaceSessionService {
  return scope.guard({
    current: () => {
      const snapshot = session.current();
      return Object.freeze({ orgId: snapshot.status === 'authenticated' ? snapshot.orgId : null });
    },
  });
}

/**
 * True when `value` is a plain JSON object: a non-null, non-array object whose prototype is
 * `Object.prototype` or `null`, which `JSON.stringify` serialises without throwing and without
 * dropping a member (no function, symbol, `undefined`, `BigInt` or non-finite number at any
 * depth, no cycle, no class instance). Pure; never throws.
 */
export function isPlainJsonObject(value: unknown): value is Readonly<Record<string, unknown>> {
  return isPlainObject(value) && isJsonData(value, new Set());
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function isJsonData(value: unknown, seen: Set<object>): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'object') return false;
  if (seen.has(value)) return false;
  seen.add(value);
  const members: readonly unknown[] = Array.isArray(value)
    ? value
    : isPlainObject(value)
      ? Object.values(value)
      : [undefined];
  const ok = members.every((member) => isJsonData(member, seen));
  seen.delete(value);
  return ok;
}
